import { components } from "@certn/api/generated-schemas/client-portal";
import {
  mq,
  SelectItem,
  Select,
  Text,
  Tab,
  TabList,
  Tabs,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageTitle } from "../../../../components/page-title/PageTitle";
import { LAST_SELECTED_CLIENT_GROUP_KEY } from "../../../../utils/check-utils";
import { OrderContext } from "../../OrderContext";
import { useFocusStepHeading } from "../../useFocusStepHeading";
import { TabSwitchWarningModal } from "./TabSwitchWarningModal";
import { ORDER_TAB_TYPES } from "../../../../constants";

interface Props {
  customerGroups: components["schemas"]["CustomerGroupOptionsList"][];
  selectedGroup: string | null;
  setSelectedGroup: (group: string) => void;
  selectedTab: (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES];
  setSelectedTab: (
    tab: (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES]
  ) => void;
  showQuickscreen: boolean;
}

export function ContentHeader({
  customerGroups,
  selectedGroup,
  setSelectedGroup,
  selectedTab,
  setSelectedTab,
  showQuickscreen,
}: Props) {
  const { t } = useTranslation();
  const { order, setOrder } = useContext(OrderContext);
  const headingId = useFocusStepHeading();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<
    (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES] | null
  >(null);

  useEffect(() => {
    // Set the order with the default selected group if available
    if (selectedGroup && !order.group) {
      setOrder({
        ...order,
        group: selectedGroup,
      });
    }
  }, [selectedGroup, order, setOrder]);

  const handleTabChange = (
    newTab: (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES]
  ) => {
    // If switching from quickscreen to applicant-submission and cart has items, show modal
    if (
      selectedTab === ORDER_TAB_TYPES.QUICKSCREEN &&
      newTab === ORDER_TAB_TYPES.APPLICANT_SUBMISSION &&
      (order.checks.length > 0 || order.bundles.length > 0)
    ) {
      setPendingTab(newTab);
      setIsModalOpen(true);
    } else {
      // For all other cases (including applicant-submission to quickscreen), clear cart and switch
      clearCartAndSwitchTab(newTab);
    }
  };

  const clearCartAndSwitchTab = (
    newTab: (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES]
  ) => {
    setOrder({
      ...order,
      checks: [],
      bundles: [],
      cv_review_id: null,
    });
    setSelectedTab(newTab);
  };

  const handleConfirmSwitch = () => {
    if (pendingTab) {
      clearCartAndSwitchTab(pendingTab);
    }
    setIsModalOpen(false);
    setPendingTab(null);
  };

  const handleCancelSwitch = () => {
    setIsModalOpen(false);
    setPendingTab(null);
  };

  return (
    <div className={styles.headerContainer}>
      <PageTitle
        headingId={headingId}
        actions={
          <div>
            <Select
              items={customerGroups}
              testID="ordering-group-select"
              selectedKey={selectedGroup}
              isRequired
              name="group"
              aria-label={t("Group")}
              onSelectionChange={(key) => {
                setSelectedGroup(key?.toString() ?? "");
                localStorage.setItem(
                  LAST_SELECTED_CLIENT_GROUP_KEY,
                  key?.toString() ?? ""
                );
                setOrder({
                  ...order,
                  group: key?.toString() ?? "",
                });
              }}
            >
              {(item) => (
                <SelectItem
                  id={item.id}
                  textValue={item.name}
                  key={item.id}
                  data-testid={slugify(item.name)}
                >
                  {item.name}
                </SelectItem>
              )}
            </Select>
          </div>
        }
      >
        {t("Select checks")}
      </PageTitle>
      <Text color="text-weak">
        {t("Choose which checks you'd like to add to your order.")}
      </Text>

      {showQuickscreen && (
        <>
          <Tabs
            testID="check-selection-tabs"
            selectedKey={selectedTab}
            onSelectionChange={(key) =>
              handleTabChange(
                key as (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES]
              )
            }
          >
            <TabList aria-label={t("Check selection")}>
              <Tab
                id={ORDER_TAB_TYPES.APPLICANT_SUBMISSION}
                testID="applicant-submission-tab"
              >
                {t("Applicant submission")}
              </Tab>
              <Tab id={ORDER_TAB_TYPES.QUICKSCREEN} testID="quickscreen-tab">
                {t("Quickscreen")}
              </Tab>
            </TabList>
          </Tabs>
          <TabSwitchWarningModal
            isOpen={isModalOpen}
            onClose={handleCancelSwitch}
            onConfirm={handleConfirmSwitch}
          />
        </>
      )}
    </div>
  );
}

const styles = {
  headerContainer: css(
    {
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-3)",
    },
    mq({
      padding: ["0", "0", "var(--space-5) var(--space-8)"],
    })
  ),
};
